// ============================================
// CENTRALIZED SELECTORS
// ============================================
// This file exports all selectors used throughout the application.
// Provider-specific selectors come from providerInterface.js, which
// accesses state.game and state.spin (populated by store.js based on provider).
// Shared selectors are exported directly from their respective slices.

// ============================================
// PROVIDER-SPECIFIC SELECTORS
// ============================================
// These work with any provider because store.js loads the correct slice
export {
  selectIsSpinning,
  selectSpinComplete,
  selectWindupComplete,
  selectReelsFrozen,
  selectManualStop,
  selectSessionExpired,
  selectCurrentRound,
  selectWinningLines,
  selectScatterCount,
  selectScatterPositions,
  selectWinType,
  selectBalance,
  selectBet,
  selectPaytable,
  selectCurrency,
  selectCurrencyConfig,
  selectBonusBuyMultiplier,
  selectDisplayedBalance,
  selectBets,
  selectGameStatus,
  selectAssetsStatus,
  selectGameMode,
  selectFeatureState,
  selectReloadState,
  selectAvailableActions,
  selectFlowState,
  selectFeatures,
  selectLastCommand,
  selectErrorCode,
} from "./Slices/providerInterface";

// Feature game selectors
export const selectHoldAndWinActive = (state) =>
  state.featureGame.holdAndWinActive;
export const selectHoldAndWinInitialReels = (state) =>
  state.featureGame.initialReels;
export const selectHoldAndWinCurrentRound = (state) =>
  state.featureGame.currentRound;
export const selectHoldAndWinCoinMultipliers = (state) =>
  state.featureGame.currentRound?.storage?.coin_multipliers || null;
export const selectRespinsLeft = (state) => state.featureGame.respinsLeft;
export const selectProgressBarCount = (state) =>
  state.featureGame.progressBarCount;
export const selectHoldAndWinSymbolsReady = (state) =>
  state.featureGame.holdAndWinSymbolsReady;
export const selectHoldAndWinReelsReady = (state) =>
  state.featureGame.holdAndWinReelsReady;
export const selectHoldAndWinOverlayReelsReady = (state) =>
  state.featureGame.holdAndWinOverlayReelsReady;
export const selectIsHoldAndWinIntroReload = (state) =>
  state.featureGame.isHoldAndWinIntroReload;
export const selectIsHoldAndWinMidFeatureReload = (state) =>
  state.featureGame.isHoldAndWinMidFeatureReload;
export const selectIsHoldAndWinCompletionReload = (state) =>
  state.featureGame.isHoldAndWinCompletionReload;
export const selectTriggerRoundWin = (state) =>
  state.featureGame.triggerRound?.win || 0;
export const selectFreeSpinTriggered = (state) =>
  state.featureGame.freeSpinTriggered;
export const selectFreeSpinActive = (state) =>
  state.featureGame.freeSpinActive;
export const selectFreeSpinSpinning = (state) =>
  state.featureGame.freeSpinSpinning;
export const selectFreeSpinComplete = (state) =>
  state.featureGame.freeSpinComplete;

// Feature spin state selectors (generic for any feature with parallel spin cycle)
export const selectFeatureSpinActive = (state) => state.spin.featureSpinActive;
export const selectFeatureSpinComplete = (state) =>
  state.spin.featureSpinComplete;
export const selectFeatureSpinStatus = (state) => state.spin.featureSpinStatus;
export const selectFeatureSpinManualStop = (state) =>
  state.spin.featureSpinManualStop;

// ============================================
// USER INTERFACE SELECTORS
// ============================================
export {
  selectMenuActive,
  selectIntroPopupActive,
  selectUserInterfaceDisabled,
  selectPaytableActive,
  selectGameRulesActive,
  selectSettingsActive,
  selectCreditsPopupActive,
  selectExpirationPopupActive,
  selectBuyFreeSpinsPopupActive,
  selectConnectionPopupActive,
  selectSoundAdjustmentPopupActive,
  selectSymbolPopupActive,
  selectSymbolPopupBlocked,
  selectInfoOverlayPopupActive,
  selectTurboSuggestionPopupActive,
  selectAutoplayPopupActive,
  selectBetSelectPopupActive,
  selectBonusBuyActive,
  selectSpacebarSpinEnabled,
  selectAnyPopupActive,
  selectHoldAndWinResumePopupActive,
  selectErrorPopupActive,
  selectErrorPopupCode,
  selectInfoPopupActive,
  selectInfoPopupKey,
  selectInfoPopupData,
  selectIosFullscreenOverlayActive,
  selectLeaveGamePopupActive,
} from "./Slices/userInterfaceSlice";

// ============================================
// SETTINGS SELECTORS
// ============================================
export {
  selectAutoPlaySpeed,
  selectAutoPlayActive,
  selectRemainingAutoSpins,
  selectAutoSpinCount,
  selectPendingAutoPlaySpeed,
  selectSimulationModeEnabled,
  selectBuyFreeSpinsMode,
  selectSoundLevelAdjustmentEnabled,
  selectManualStopCount,
  selectManualStopTrackingEnabled,
  selectTurboSuggestionPending,
  selectStopOnAnyWin,
  selectStopOnBonusGame,
  selectStopOnSingleWinExceeds,
  selectStopOnBalanceIncreases,
  selectStopOnBalanceDecreases,
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
} from "./Slices/settingsSlice";

// ============================================
// ANIMATION SELECTORS
// ============================================
export {
  selectIntroTransitionActive,
  selectShowGameAfterTransition,
  selectErrorRecoveryTransitionActive,
  selectErrorRecoveryScreenCovered,
  selectResetMainGameVisualEffects,
  selectCounterAnimationFinished,
  selectLinesRotationFinished,
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
  selectCriticalAnimationPlaying,
  selectUserWantsToDismissWinAnimation,
  selectSkipWinAnimation,
  selectIsWaitingForUserConfirmation,
  selectAnimatedWinAmount,
  selectCounterAnimationStarted,
  selectSkipCounterAnimation,
  selectShowHoldAndWinIntro,
  selectSceneTransitionActive,
  selectShowHoldAndWinOutro,
  selectShowHoldAndWinCollector,
  selectShowMaxWin,
  selectExpandingWildsOccurred,
  selectFreeSpinTransitionActive,
  selectFreeSpinTransitionMidpoint,
  selectShowFreeSpinIntro,
  selectFreeSpinCounterVisible,
} from "./Slices/animationsSlice";

// ============================================
// SOUND SELECTORS
// ============================================
export { selectMasterVolume } from "./Slices/soundsSlice";


// ============================================
// FREE ROUNDS SELECTORS
// ============================================
export {
  selectFreeRoundsActive,
  selectFreeRoundsCount,
  selectFreeRoundsPerformed,
  selectFreeRoundsTotalWin,
  selectFreeRoundsBet,
  selectFreeRoundsStatus,
  selectFreeRoundsCompletionPending,
  selectFreeRoundsEnded,
  selectFreeRoundsReloadPending,
  selectIsFreeRoundsCompletionReload,
  selectBalanceAlreadyUpdated,
} from "./Slices/freeRoundsSlice";
